import { useCallback, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import { CompanyService, RetrieveDocumentGenerationJob } from '../../api';
import { useStore } from '../../common/store';
import { UseStoreValues } from '../../common/types/store';
import { EventType, useEventBus } from '../../context';
import { useAsyncProcessor } from '../../context/AsyncProcessorContext/AsyncProcessorContext';
import { Logger } from '../../utilities';

export enum POLL_TYPE {
  GENERATION_JOBS = 'generation_jobs'
}

/**
 * Hook that handles app-level async actions on load.
 * This includes initializing polling for pending generation jobs.
 */
export const useAppLoadAsyncOperations = (initOnLoad = true) => {
  const { addJob, removeJob } = useAsyncProcessor();
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { companyInfo } = storeValue;
  const { publish } = useEventBus();
  
  const initializeGenerationJobs = useCallback(async () => {
    try {
      if (!companyInfo?.id) return;

      // Fetch FINANCIAL_MAPPING jobs with PENDING or IN_PROGRESS status directly from API
      // We make multiple calls because the API doesn't support OR conditions for status
      const [pendingJobs, inProgressJobs] = await Promise.all([
        CompanyService.apiCompanyDocumentGenerationJobsList(
          companyInfo.id!.toString(),
          undefined,
          undefined,
          RetrieveDocumentGenerationJob.status.PENDING,
          RetrieveDocumentGenerationJob.processing_type.FINANCIAL_MAPPING
        ),
        CompanyService.apiCompanyDocumentGenerationJobsList(
          companyInfo.id!.toString(),
          undefined,
          undefined,
          RetrieveDocumentGenerationJob.status.IN_PROGRESS,
          RetrieveDocumentGenerationJob.processing_type.FINANCIAL_MAPPING
        ),
      ]);

      // Combine results from both calls
      const activeJobs = [...(pendingJobs || []), ...(inProgressJobs || [])];

      if (activeJobs.length > 0) {
        Logger.info('useAppLoadAsyncActions:', 'Found active FINANCIAL_MAPPING jobs to poll', activeJobs.length);
      }

      // Add each active job to polling
      activeJobs.forEach((job: RetrieveDocumentGenerationJob) => {
        addJob({
          id: job.job_id,
          details: {
            title: 'Financial Mapping',
            description: `Processing job ${job.job_id}`,
          },
          pollingConfig: {
            pollingFunction: async () => {
              const result = await CompanyService.apiCompanyDocumentGenerationJobsRead(
                companyInfo.id!.toString(),
                job.job_id
              );
              return result;
            },
            pollingInterval: 5000,
            pollingArgs: [],
          },
          onResult: async (result: RetrieveDocumentGenerationJob) => {
            if (result.status === RetrieveDocumentGenerationJob.status.COMPLETED) {
              try {
                // Auto-create the mapping
                await CompanyService.apiGenerationJobsCreateAttributeMapping(result.job_id);
                publish(EventType.FIN_MAP_ATT_MAPPER_UPDATED_VIA_GEN_JOB_COMPLETED, result.job_id);
                enqueueSnackbar('Financial mapping completed and created', { variant: 'success' });
              } catch (error) {
                Logger.error('useAppLoadAsyncActions - Error creating mapping for completed job', error);
                enqueueSnackbar('Job completed but mapping creation failed', { variant: 'warning' });
              }
              removeJob(result.job_id);
            } else if (result.status === RetrieveDocumentGenerationJob.status.FAILED) {
              enqueueSnackbar('Financial mapping job failed', { variant: 'error' });
              removeJob(result.job_id);
            }
          },
          onError: (error: Error) => {
            Logger.error('useAppLoadAsyncActions - Generation job polling error', error);
          },
        });
      });
    } catch (error) {
      Logger.error('useAppLoadAsyncActions - Error initializing jobs from API', error);
    }
  }, [companyInfo, addJob, removeJob, publish]);

  const manuallyExecutePoll = (pollType: POLL_TYPE.GENERATION_JOBS) => {
    if (pollType === POLL_TYPE.GENERATION_JOBS) {
      initializeGenerationJobs()
    }
  }

  useEffect(() => {
    if (initOnLoad) {
      initializeGenerationJobs();
    }
  }, [initOnLoad, initializeGenerationJobs]);

  return {
    manuallyExecutePoll
  }
};

export default useAppLoadAsyncOperations;
